/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled from "styled-components";
import { CircleCheck, HardDrive, ScanSearch } from "lucide-react";

import { LIVE } from "@src/components/modules/LiveModule/liveUtils";
import { Bar } from "@src/components/modules/LiveModule/ui/LiveUi";
import { DiskRow, formatBytes, formatDuration, formatRate } from "@src/utils/TransferProgress";

/* One row per disk: name and how it is copied, size, bar, figures, speed and time left. */

const Table = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1.6fr) 72px minmax(140px, 2fr) 92px 82px;
  column-gap: 16px;
  align-items: center;
  font-size: 13px;
  @media (max-width: 900px) {
    grid-template-columns: minmax(120px, 1fr) minmax(120px, 1.4fr) 80px;
    .hide-narrow {
      display: none;
    }
  }
  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) 64px;
    column-gap: 10px;
  }
`;
const Head = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${LIVE.muted};
  padding: 0 0 6px 0;
  border-bottom: 1px solid ${LIVE.border};
  &.num {
    text-align: right;
  }
`;
const Cell = styled.div`
  padding: 9px 0;
  border-bottom: 1px solid var(--t9-bg-sunken);
  min-width: 0;
  font-variant-numeric: tabular-nums;
  color: ${LIVE.textSecondary};
  &.num {
    text-align: right;
    white-space: nowrap;
  }
`;
const DiskName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  svg {
    flex-shrink: 0;
  }
`;
const NameText = styled.div`
  min-width: 0;
  div {
    color: ${LIVE.text};
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  span {
    display: block;
    font-size: 11.5px;
    color: ${LIVE.muted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const BarCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  small {
    font-size: 11.5px;
    color: ${LIVE.muted};
    display: flex;
    justify-content: space-between;
    gap: 8px;
    white-space: nowrap;
    b {
      color: ${LIVE.text};
      font-weight: 600;
    }
  }
`;

type Props = {
  disks: DiskRow[];
  /** Prefix disk names with their instance (runs with several machines). */
  showInstance?: boolean;
  max?: number;
};

const shortInstance = (name: string) => name.split("/").pop() || name;

const describe = (d: DiskRow, showInstance: boolean) => {
  const bits: string[] = [];
  if (showInstance) {
    bits.push(shortInstance(d.instance));
  }
  if (d.waiting) {
    bits.push("waiting to start");
  } else if (d.scanning) {
    bits.push(d.mode || "scanning");
  } else if (d.mode) {
    bits.push(d.mode === "full" ? "full copy" : d.mode);
  }
  if (d.via) {
    bits.push(d.via);
  }
  return bits.join(" · ");
};

const DiskRows = ({ disks, showInstance = false, max }: Props) => {
  const rows = max ? disks.slice(0, max) : disks;
  return (
    <Table role="table" aria-label="Disks">
      <Head role="columnheader">Disk</Head>
      <Head role="columnheader" className="num hide-narrow">
        Size
      </Head>
      <Head role="columnheader">Progress</Head>
      <Head role="columnheader" className="num hide-narrow">
        Speed
      </Head>
      <Head role="columnheader" className="num">
        Time left
      </Head>
      {rows.map(d => {
        const tone = d.complete ? "success" : d.waiting ? "neutral" : "info";
        const Icon = d.complete ? CircleCheck : d.scanning ? ScanSearch : HardDrive;
        const iconColour = d.complete ? LIVE.greenDeep : d.active ? LIVE.cyanDeep : LIVE.faint;
        const total = d.totalBytes != null ? d.totalBytes : d.diskSizeBytes;
        return (
          <React.Fragment key={`${d.instance}:${d.disk}`}>
            <Cell role="cell">
              <DiskName>
                <Icon size={16} color={iconColour} strokeWidth={1.9} />
                <NameText>
                  <div title={d.disk}>{d.disk}</div>
                  <span title={describe(d, showInstance)}>{describe(d, showInstance)}</span>
                </NameText>
              </DiskName>
            </Cell>
            <Cell role="cell" className="num hide-narrow">
              {formatBytes(d.diskSizeBytes)}
            </Cell>
            <Cell role="cell">
              <BarCell>
                <Bar percent={d.percent} tone={tone} height={6} moving={d.active} label={`${d.disk} ${Math.round(d.percent)}%`} />
                <small>
                  <span>
                    {d.complete
                      ? `${formatBytes(d.doneBytes)} ${d.scanning ? "read" : "copied"}${
                          d.durationSeconds != null ? ` in ${formatDuration(d.durationSeconds)}` : ""
                        }`
                      : d.waiting
                        ? "Not started"
                        : `${formatBytes(d.doneBytes)} of ${formatBytes(total)}${d.position ? " of the disk" : ""}`}
                  </span>
                  <b>{Math.floor(d.percent)}%</b>
                </small>
              </BarCell>
            </Cell>
            <Cell role="cell" className="num hide-narrow">
              {d.complete ? (
                <span style={{ color: LIVE.muted }}>avg {formatRate(d.rateBytesPerSec)}</span>
              ) : d.rateBytesPerSec && d.active ? (
                <span style={{ color: LIVE.text, fontWeight: 600 }}>{formatRate(d.rateBytesPerSec)}</span>
              ) : (
                "—"
              )}
            </Cell>
            <Cell role="cell" className="num">
              {d.complete ? (
                <span style={{ color: LIVE.greenDeep, fontWeight: 600 }}>Done</span>
              ) : d.etaSeconds != null && d.active ? (
                `~${formatDuration(d.etaSeconds)}`
              ) : (
                "—"
              )}
            </Cell>
          </React.Fragment>
        );
      })}
    </Table>
  );
};

export default DiskRows;
